import type { DayKey, MonthKey } from './types'

/** Calendar helpers over `yyyy-MM-dd` / `yyyy-MM` keys. Padded keys compare with `<` / `>`. */

export interface YearMonth {
  year: number
  /** 1..12 */
  month: number
}

const pad2 = (n: number) => String(n).padStart(2, '0')

export function monthKey(ym: YearMonth): MonthKey {
  return `${ym.year}-${pad2(ym.month)}`
}

export function dayKey(year: number, month: number, day: number): DayKey {
  return `${year}-${pad2(month)}-${pad2(day)}`
}

export function dateToDayKey(d: Date): DayKey {
  return dayKey(d.getFullYear(), d.getMonth() + 1, d.getDate())
}

export function parseDayKey(key: DayKey): { year: number; month: number; day: number } {
  const [y, m, d] = key.split('-')
  return { year: Number(y), month: Number(m), day: Number(d) }
}

export function parseMonthKey(key: MonthKey): YearMonth {
  const [y, m] = key.split('-')
  return { year: Number(y), month: Number(m) }
}

export function monthKeyOfDay(day: DayKey): MonthKey {
  return day.slice(0, 7)
}

export function yearMonthOfDay(day: DayKey): YearMonth {
  return parseMonthKey(monthKeyOfDay(day))
}

export function addMonths(ym: YearMonth, delta: number): YearMonth {
  const zeroBased = ym.year * 12 + (ym.month - 1) + delta
  const year = Math.floor(zeroBased / 12)
  const month = zeroBased - year * 12 + 1
  return { year, month }
}

/** Inclusive list of months from `from` to `to`; empty when `from` is after `to`. */
export function monthRange(from: YearMonth, to: YearMonth): YearMonth[] {
  const out: YearMonth[] = []
  for (let ym = from; monthKey(ym) <= monthKey(to); ym = addMonths(ym, 1)) out.push(ym)
  return out
}

/** 0 = Sunday … 6 = Saturday, computed without a timezone. */
export function weekdayOf(day: DayKey): number {
  const { year, month, day: d } = parseDayKey(day)
  return new Date(Date.UTC(year, month - 1, d)).getUTCDay()
}

/** Strict `yyyy-MM-dd` that names a real calendar day. */
export function isDayKey(value: unknown): value is DayKey {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const { year, month, day } = parseDayKey(value)
  if (month < 1 || month > 12 || day < 1) return false
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export function isMonthKey(value: unknown): value is MonthKey {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}$/.test(value)) return false
  const m = Number(value.slice(5))
  return m >= 1 && m <= 12
}

/** `HH:mm`, 00:00 to 23:59. */
export function isTimeOfDay(value: unknown): value is string {
  return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
}
